import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Chip,
  Divider,
  Paper,
  CircularProgress,
  IconButton,
  Grid,
} from "@mui/material";
import {
  Close as CloseIcon,
  Person as PersonIcon,
  AssignmentTurnedIn as AcceptIcon,
  LocalHospital as HospitalIcon,
  ScienceOutlined as LabIcon,
  AccessTime as TimeIcon,
  NotesOutlined as NotesIcon,
} from "@mui/icons-material";
import { referralService } from "../../services/lis";

export default function ReferralDetailsDialog({
  open,
  onClose,
  notification,
  onAcceptSuccess,
  isDark,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!notification) return null;

  const cardBg = isDark ? "#1E1E1E" : "#FFFFFF";
  const innerBg = isDark ? "#121212" : "#F8FAFC";
  const textPrimary = isDark ? "#F8FAFC" : "#0F172A";
  const textSecondary = isDark ? "#94A3B8" : "#64748B";
  const borderColor = isDark ? "rgba(255,255,255,0.08)" : "#E2E8F0";

  const details = notification.referralDetails || {};
  const requestedTests = details.requestedTests || [];
  const snapshot = notification.patientSnapshot || {};
  const urgency = (details.urgency || notification.priority || "routine").toUpperCase();

  const handleAccept = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await referralService.acceptReferral(notification._id);
      onAcceptSuccess?.(res.data, notification);
    } catch (err) {
      console.error("Failed to accept referral:", err);
      setError(err.response?.data?.error || "Failed to accept referral");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          bgcolor: cardBg,
          borderRadius: "14px",
          border: `1px solid ${borderColor}`,
          boxShadow: isDark
            ? "0 20px 50px rgba(0,0,0,0.6)"
            : "0 20px 50px rgba(15,23,42,0.12)",
          width: { xs: '95vw', sm: '92vw', md: '820px', lg: '860px' },
          maxWidth: '880px !important',
          maxHeight: '88vh',
        },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${borderColor}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              bgcolor: "rgba(124, 58, 237, 0.12)",
              color: "#7C3AED",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LabIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", color: textPrimary, fontFamily: "'Satoshi', sans-serif" }}>
              Lab Test Request
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", color: textSecondary }}>
              Incoming clinical referral from Radiology
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: textSecondary, p: '3px' }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 2, py: 1.5 }}>
        {error && (
          <Paper
            sx={{
              p: 1,
              mb: 1.5,
              bgcolor: isDark ? "rgba(239, 68, 68, 0.15)" : "#FEF2F2",
              border: "1px solid #EF4444",
              borderRadius: "8px",
            }}
          >
            <Typography sx={{ fontSize: "0.78rem", color: "#EF4444", fontWeight: 600 }}>
              {error}
            </Typography>
          </Paper>
        )}

        <Grid container spacing={2}>
          {/* Left Column: Patient & Referral Meta */}
          <Grid item xs={12} sm={6}>
            {/* Patient Card */}
            <Paper
              sx={{
                p: 1.5,
                mb: 1.5,
                bgcolor: innerBg,
                borderRadius: "10px",
                border: `1px solid ${borderColor}`,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <PersonIcon sx={{ color: "#7C3AED", fontSize: 18 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: "0.88rem", color: textPrimary }}>
                    {notification.patientName || `${snapshot.firstName || ''} ${snapshot.lastName || ''}` || "Patient"}
                  </Typography>
                </Box>
                <Chip
                  label={urgency}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.65rem",
                    height: 22,
                    bgcolor:
                      urgency === "STAT"
                        ? "#DC2626"
                        : urgency === "URGENT"
                        ? "#EA580C"
                        : "#2563EB",
                    color: "#FFFFFF",
                    borderRadius: "6px",
                  }}
                />
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mt: 1 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: textSecondary, fontSize: "0.7rem", display: "block" }}>MRN</Typography>
                  <Typography variant="body2" sx={{ color: textPrimary, fontWeight: 600, fontSize: "0.8rem" }}>{snapshot.mrn || "—"}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: textSecondary, fontSize: "0.7rem", display: "block" }}>Gender</Typography>
                  <Typography variant="body2" sx={{ color: textPrimary, fontWeight: 600, fontSize: "0.8rem" }}>{snapshot.sex === "M" ? "Male" : snapshot.sex === "F" ? "Female" : snapshot.sex || "—"}</Typography>
                </Box>
                {snapshot.birthDate && (
                  <Box sx={{ gridColumn: "span 2" }}>
                    <Typography variant="caption" sx={{ color: textSecondary, fontSize: "0.7rem", display: "block" }}>Date of Birth</Typography>
                    <Typography variant="body2" sx={{ color: textPrimary, fontWeight: 600, fontSize: "0.8rem" }}>{new Date(snapshot.birthDate).toLocaleDateString()}</Typography>
                  </Box>
                )}
              </Box>
            </Paper>

            {/* Referral Meta Card */}
            <Paper
              sx={{
                p: 1.5,
                bgcolor: innerBg,
                borderRadius: "10px",
                border: `1px solid ${borderColor}`,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                <Typography sx={{ fontSize: "0.75rem", color: textSecondary, display: "flex", alignItems: "center", gap: 0.75 }}>
                  <HospitalIcon sx={{ fontSize: 16, color: "#7C3AED" }} /> Requested by: <strong style={{ color: textPrimary }}>{notification.senderName || "Radiology"}</strong>
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", color: textSecondary, display: "flex", alignItems: "center", gap: 0.75 }}>
                  <TimeIcon sx={{ fontSize: 16, color: "#7C3AED" }} /> {notification.timestamp ? new Date(notification.timestamp).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : "Recently"}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Right Column: Tests & Notes */}
          <Grid item xs={12} sm={6}>
            {/* Requested Tests Section */}
            <Box sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: textSecondary, mb: 0.75, textTransform: "uppercase" }}>
                Requested Laboratory Tests
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {requestedTests.length > 0 ? (
                  requestedTests.map((t, idx) => (
                    <Chip
                      key={idx}
                      label={t}
                      size="small"
                      sx={{
                        bgcolor: isDark ? "rgba(124, 58, 237, 0.2)" : "#EDE9FE",
                        color: isDark ? "#C4B5FD" : "#6D28D9",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        borderRadius: "6px",
                      }}
                    />
                  ))
                ) : (
                  <Typography sx={{ fontSize: "0.78rem", color: textSecondary }}>
                    No specific test list provided.
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Clinical Notes & Details */}
            <Paper
              sx={{
                p: 1.5,
                bgcolor: innerBg,
                borderRadius: "10px",
                border: `1px solid ${borderColor}`,
              }}
            >
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: textSecondary, mb: 0.5, display: "flex", alignItems: "center", gap: 0.5, textTransform: "uppercase" }}>
                <NotesIcon sx={{ fontSize: 14 }} /> Clinical Reason & Notes
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", color: textPrimary, fontStyle: details.clinicalNotes ? "normal" : "italic", lineHeight: 1.4 }}>
                {details.clinicalNotes || "No additional clinical notes provided."}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ px: 2, py: 1.25, borderTop: `1px solid ${borderColor}`, justifyContent: "space-between" }}>
        <Button
          onClick={onClose}
          sx={{
            textTransform: "none",
            color: textSecondary,
            fontWeight: 600,
            fontSize: "0.8rem",
            height: 30
          }}
        >
          Dismiss
        </Button>
        <Button
          variant="contained"
          onClick={handleAccept}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <AcceptIcon sx={{ fontSize: 16 }} />}
          sx={{
            bgcolor: "#7C3AED",
            color: "#FFFFFF",
            fontWeight: 700,
            textTransform: "none",
            borderRadius: "6px",
            px: 2,
            height: 30,
            fontSize: "0.8rem",
            boxShadow: "0 4px 14px rgba(124, 58, 237, 0.35)",
            "&:hover": { bgcolor: "#6D28D9" },
          }}
        >
          {loading ? "Accepting..." : "Accept & Create Lab Order"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
